import { useState, type FormEvent } from 'react';
import { Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { buildSsoConsentUrl } from '@siverix/shared';
import { useAuth } from '../auth/AuthContext';
import { useServerInfo } from '../hooks/useServerInfo';
import { returnPathFrom } from '../auth/returnPath';

export function LoginPage(): JSX.Element {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { info } = useServerInfo();
  const [emailOrUsername, setId] = useState('');
  const [password, setPwd] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const returnTo = returnPathFrom(location.state);
  // [2.13.0] Serveur Siverix rattaché à un serveur de comptes : ses comptes
  // arrivent par le compte Siverix et n'ont pas de mot de passe local.
  const ssoIssuer = info?.standalone
    ? info.ssoIssuers?.find((i) => /^https?:\/\//i.test(i))
    : undefined;

  if (user) return <Navigate to={returnTo} replace />;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await login(emailOrUsername, password);
      navigate(returnTo, { replace: true });
    } catch (e: unknown) {
      setErr((e as Error).message ?? 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }

  function loginWithSiverix(issuer: string): void {
    window.location.assign(buildSsoConsentUrl(issuer, window.location.origin));
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <form
        onSubmit={submit}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '32px',
          width: '380px',
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: '22px' }}>🔮 Connexion Siverix</h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>
          Entre tes identifiants pour accéder à l'administration.
        </p>

        {ssoIssuer && (
          <>
            <button
              type="button"
              onClick={() => loginWithSiverix(ssoIssuer)}
              style={{ ...primaryButtonStyle, marginTop: '12px' }}
            >
              Se connecter avec mon compte Siverix
            </button>
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--color-text-muted)' }}>
              Tu passes par {hostOf(ssoIssuer)}, qui te demande de confirmer, puis tu reviens ici.
            </p>
            <div
              aria-hidden
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '18px 0 0',
                color: 'var(--color-text-muted)',
                fontSize: '12px',
              }}
            >
              <span style={{ flex: 1, borderTop: '1px solid var(--color-border)' }} />
              ou avec un identifiant
              <span style={{ flex: 1, borderTop: '1px solid var(--color-border)' }} />
            </div>
          </>
        )}

        <label style={{ display: 'block', fontSize: '12px', marginTop: '16px' }}>
          Email ou pseudo
          <input
            type="text"
            value={emailOrUsername}
            onChange={(e) => setId(e.target.value)}
            required
            autoFocus={!ssoIssuer}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'block', fontSize: '12px', marginTop: '12px' }}>
          Mot de passe
          <input
            type="password"
            value={password}
            onChange={(e) => setPwd(e.target.value)}
            required
            style={inputStyle}
          />
        </label>

        {err && (
          <div
            role="alert"
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              borderRadius: '6px',
              background: 'rgba(248,81,73,.15)',
              color: 'var(--color-error)',
              fontSize: '13px',
            }}
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={ssoIssuer ? secondaryButtonStyle : primaryButtonStyle}
        >
          {loading ? '…' : 'Se connecter'}
        </button>

        <p style={{ marginTop: '16px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
          Pas de compte ? <Link to="/signup">Créer un compte</Link>
        </p>
      </form>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '4px',
  padding: '10px 12px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: '20px',
  width: '100%',
  padding: '12px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
};
